import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  TrendingUp, Wallet, ArrowRight, Lock, ChevronRight
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WalletConnectModal from '../components/WalletConnectModal';
import { useAuth } from '../App';
import toast from 'react-hot-toast';
import './Staking.css';

const stakingPools = [
  {
    name: 'Ethereum',
    symbol: 'ETH',
    logo: '/Crypto-logos/Ethereum-logo.webp',
    apy: '5.2%',
    tvl: '$1.2B',
    minStake: '0.1 ETH',
    lockPeriod: 'Flexible',
    popular: true
  },
  {
    name: 'Bitcoin',
    symbol: 'BTC',
    logo: '/Crypto-logos/Bitcoin-Logo.webp',
    apy: '3.8%',
    tvl: '$890M',
    minStake: '0.01 BTC',
    lockPeriod: '30 Days'
  },
  {
    name: 'Solana',
    symbol: 'SOL',
    logo: '/Crypto-logos/Solana-logo.webp',
    apy: '7.5%',
    tvl: '$450M',
    minStake: '1 SOL',
    lockPeriod: 'Flexible'
  },
  {
    name: 'BNB',
    symbol: 'BNB',
    logo: '/Crypto-logos/bnb-logo.webp',
    apy: '6.1%',
    tvl: '$320M',
    minStake: '0.5 BNB',
    lockPeriod: '14 Days'
  },
  {
    name: 'Polygon',
    symbol: 'MATIC',
    logo: '/Crypto-logos/matic-token-icon.webp',
    apy: '8.2%',
    tvl: '$180M',
    minStake: '50 MATIC',
    lockPeriod: 'Flexible'
  },
  {
    name: 'Avalanche',
    symbol: 'AVAX',
    logo: '/Crypto-logos/Chainlink-logo.webp',
    apy: '9.4%',
    tvl: '$95M',
    minStake: '5 AVAX',
    lockPeriod: '21 Days'
  }
];

const Staking = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { registerUser } = useAuth();
  const navigate = useNavigate();

  const handleWalletConnect = async (walletData) => {
    try {
      await registerUser(walletData);
      sessionStorage.setItem('walletJustConnected', 'true');
      setIsModalOpen(false);
      toast.success('Account created successfully!');
      navigate('/backup-success');
    } catch (error) {
      toast.error('Could not create account. Please try again.');
    }
  };

  return (
    <div className="staking-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="staking-hero">
        <div className="hero-glow"></div>
        <div className="container">
          <motion.div 
            className="staking-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-badge">
              <TrendingUp size={14} />
              Earn Passive Income
            </span>
            <h1>Stake Your Crypto.<br /><span className="text-gradient">Earn Rewards.</span></h1>
            <p>
              Put your digital assets to work. Earn competitive yields with flexible 
              staking options and industry-leading security.
            </p>
            
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="stat-value">$3.2B+</span>
                <span className="stat-label">Total Value Locked</span>
              </div>
              <div className="hero-stat">
                <span className="stat-value">9.4%</span>
                <span className="stat-label">Max APY</span>
              </div>
              <div className="hero-stat">
                <span className="stat-value">180K+</span>
                <span className="stat-label">Active Stakers</span>
              </div>
            </div>

            <div className="hero-actions">
              <button 
                className="btn btn-primary btn-lg"
                onClick={() => setIsModalOpen(true)}
              >
                Start Staking
                <ArrowRight size={20} />
              </button>
              <Link to="/ledger" className="btn btn-secondary btn-lg">
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Staking Pools Section */}
      <section className="staking-pools-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Staking Pools</span>
            <h2>Choose Your Pool</h2>
            <p>Select from our range of staking options with competitive APY rates.</p>
          </motion.div>

          <div className="pools-grid">
            {stakingPools.map((pool, index) => (
              <motion.div
                key={pool.symbol}
                className={`pool-card ${pool.popular ? 'popular' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                {pool.popular && <span className="popular-badge">Popular</span>}
                <div className="pool-header">
                  <div className="pool-icon">
                    <img src={pool.logo} alt={pool.name} />
                  </div>
                  <div className="pool-info">
                    <h3>{pool.name}</h3>
                    <span className="pool-symbol">{pool.symbol}</span>
                  </div>
                  <div className="pool-apy">
                    <span className="apy-value">{pool.apy}</span>
                    <span className="apy-label">APY</span>
                  </div>
                </div>
                
                <div className="pool-details">
                  <div className="pool-detail">
                    <span className="detail-label">TVL</span>
                    <span className="detail-value">{pool.tvl}</span>
                  </div>
                  <div className="pool-detail">
                    <span className="detail-label">Min Stake</span>
                    <span className="detail-value">{pool.minStake}</span>
                  </div>
                  <div className="pool-detail">
                    <span className="detail-label">Lock Period</span>
                    <span className="detail-value">{pool.lockPeriod}</span>
                  </div>
                </div>

                <button 
                  className="btn btn-outline btn-block"
                  onClick={() => setIsModalOpen(true)}
                >
                  Stake {pool.symbol}
                  <ChevronRight size={16} />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-staking-works">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Simple Process</span>
            <h2>How Staking Works</h2>
            <p>Start earning rewards in just three simple steps.</p>
          </motion.div>

          <div className="steps-grid">
            <motion.div 
              className="step-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="step-number">01</div>
              <div className="step-icon">
                <Wallet size={28} />
              </div>
              <h3>Connect Wallet</h3>
              <p>Link your Web3 wallet securely to access staking pools.</p>
            </motion.div>

            <motion.div 
              className="step-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="step-number">02</div>
              <div className="step-icon">
                <Lock size={28} />
              </div>
              <h3>Choose & Stake</h3>
              <p>Select your preferred pool and stake your assets.</p>
            </motion.div>

            <motion.div 
              className="step-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="step-number">03</div>
              <div className="step-icon">
                <TrendingUp size={28} />
              </div>
              <h3>Earn Rewards</h3>
              <p>Watch your rewards grow and withdraw anytime.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="staking-cta" style={{ backgroundImage: `url('/CTA-button-BG.png')` }}>
        <div className="container">
          <motion.div 
            className="staking-cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Ready to start earning?</h2>
            <p>Join thousands of users earning passive income with Web3 Ledger staking.</p>
            <div className="cta-actions">
              <button 
                className="btn btn-primary btn-lg"
                onClick={() => setIsModalOpen(true)}
              >
                <Wallet size={20} />
                Connect Wallet
              </button>
              <Link to="/get-started" className="btn btn-secondary btn-lg">
                Create Account
                <ArrowRight size={20} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* Wallet Connect Modal */}
      <WalletConnectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConnect={handleWalletConnect}
        mode="register"
      />
    </div>
  );
};

export default Staking;
